import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { sortObjectArray, SortOrder } from 'utils/array';

import Table from 'components/UI/Table';
import TableBody from 'components/UI/Table/TableBody';
import TableDataCell from 'components/UI/Table/TableDataCell';
import TableHeader from 'components/UI/Table/TableHeader';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import TableRow from 'components/UI/Table/TableRow';

import { EmptyMessage } from './styled';
import ArrowIcon from 'components/UI/Icon/ArrowIcon';
import { JsonData } from 'models/common';
import TableBodyCell from '../TableBodyCell';
import Icon, { IconType } from 'components/UI/Icon';

export type HeaderCell = {
  sortable: boolean;
  title: string;
};

type Props = {
  headerCells: HeaderCell[];
  bodyRows: JsonData[];
  emptyTextId?: string;
  sortIndex?: number;
  sortOrder?: SortOrder;
  onClick?: (id: string) => void;
  rowIcon?: IconType;
};

const SortableTable: FC<Props> = ({
  headerCells,
  bodyRows,
  emptyTextId,
  sortIndex = 1,
  sortOrder = SortOrder.Desc,
  onClick,
  rowIcon,
}) => {
  // State
  const [activeIndex, setActiveIndex] = useState<number>(sortIndex);
  const [activeSortOrder, setActiveSortOrder] = useState<SortOrder>(sortOrder);

  // On sort
  const onSort = useCallback(
    (active: boolean, i: number) => {
      if (active) {
        setActiveSortOrder(activeSortOrder * -1);
      } else {
        setActiveIndex(i);
      }
    },
    [activeSortOrder]
  );

  // Render arrow button
  const renderButton = useCallback(
    (link: string) => {
      if (!onClick || !link) {
        return null;
      }
      return (
        <TableDataCell onClick={() => onClick(link)} isPressable>
          {!rowIcon && (
            <ArrowIcon
              size="small"
              direction="right"
              transition={false}
              themeType="grey6"
            />
          )}
          {rowIcon && <Icon size="small" type={rowIcon} themeType="grey6" />}
        </TableDataCell>
      );
    },
    [onClick, rowIcon]
  );

  if (!bodyRows.length && emptyTextId) {
    return (
      <EmptyMessage>
        <FormattedMessage id={emptyTextId} />
      </EmptyMessage>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {headerCells.map((cell, index) => {
            const isActive = index === activeIndex;
            return (
              <TableHeaderCell
                key={index}
                sortable={cell.sortable}
                active={isActive}
                onClick={() => onSort(isActive, index)}
                sortOrder={activeSortOrder}
              >
                {cell.title && <FormattedMessage id={cell.title} />}
              </TableHeaderCell>
            );
          })}
          {onClick && <TableHeaderCell />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortObjectArray(
          bodyRows,
          Object.keys(bodyRows[0])[activeIndex],
          activeSortOrder
        ).map((item, rowIndex) => {
          return (
            <TableRow key={rowIndex}>
              {Object.entries(item).map(([name, value], colIndex) => (
                <TableBodyCell key={colIndex} name={name} value={value} />
              ))}
              {renderButton(item.link)}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default SortableTable;
